/**
 * NoticeComposerModal — deliver a written notice on a Wisconsin contingency
 * (design §3.2, §5.3; copy §3).
 *
 *   1. Choose (buyer, when both "not satisfied" and "waive" are allowed).
 *   2. Write and preview: statement and attachments, then the exact text from
 *      `POST …/notices/preview`, the effect in plain words, and the warnings
 *      (the missing-attachment one needs an acknowledgement).
 *   3. Sign and deliver: the attestation, a typed-name signature, Deliver.
 *   4. Confirmation: the delivered time and the PDF.
 *
 * The choices come from the server's `allowed_actions`; the frontend never
 * re-implements the contract's state machine.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENTS_NOT_ALLOWED,
  MAX_ATTACHMENTS,
  MAX_UPLOAD_BYTES,
  NOTICE_ERROR_CODES as E,
  NOTICE_TYPES,
  STATEMENT_NOT_ALLOWED,
  deliverNotice,
  downloadNoticeDocument,
  noticeErrorInfo,
  previewNotice,
  uploadContingencyAttachment,
} from '../../../agents/contingencyNoticesAgent';
import ContractDialog from './ContractDialog';
import { contractCopy, hasPlaceholder } from './contractCopy';
import { deadlineDate, msUntil, noticeTitle } from './contractFormat';
import styles from './NoticeComposerModal.module.css';

const BUYER_CHOICES = [NOTICE_TYPES.NOT_SATISFIED, NOTICE_TYPES.WAIVER];
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Error codes that end the attempt: the card must be reloaded. A `stale_state`
 * sent with `retry: true` (a lost lock race; nothing was recorded) is the
 * exception: the party can try the same step again (see `isRetryable`).
 */
const FINAL_CODES = [
  E.STALE_STATE, E.TRANSACTION_TERMINATED, E.DEADLINE_PASSED, E.NOTICE_ALREADY_DELIVERED, E.CONTINGENCY_CLOSED,
  E.CONTINGENCY_NOT_IN_SIGNED_OFFER, E.CONTINGENCY_LEGACY_STATUS,
];

const ERROR_COPY_KEY = {
  [E.DEADLINE_PASSED]: 'WI.error.deadline_passed',
  [E.NOTICE_ALREADY_DELIVERED]: 'WI.error.notice_already_delivered',
  [E.TRANSACTION_TERMINATED]: 'WI.error.transaction_terminated',
  [E.STALE_STATE]: 'WI.error.stale_state',
  [E.PREVIEW_EXPIRED]: 'WI.error.preview_expired',
  [E.CONTINGENCY_NOT_IN_SIGNED_OFFER]: 'WI.error.notice_not_in_signed_offer',
  [E.CONTINGENCY_LEGACY_STATUS]: 'WI.error.notice_legacy_status',
};

/** A `409 stale_state` the server marked `retry: true`: nothing was recorded, try the same step again. */
export function isRetryable(info) {
  return Boolean(info && info.code === E.STALE_STATE && info.detail?.retry === true);
}

/** The message to show for an API error: the server's filled copy, else ours (by code, then copy_key). */
export function composerErrorMessage(info, contract) {
  if (!info) return null;
  if (info.detail?.message) return info.detail.message;
  const key = ERROR_COPY_KEY[info.code] || (info.copyKey && contractCopy(info.copyKey) ? info.copyKey : null);
  if (key) return contractCopy(key, { deadline_date: deadlineDate(contract) });
  return info.message;
}

/** The effect in plain words: the server's filled copy, else the copy key if it needs no values. */
export function effectMessage(effect) {
  if (!effect) return null;
  if (effect.message) return effect.message;
  const text = effect.copy_key ? contractCopy(effect.copy_key) : null;
  return text && !hasPlaceholder(text) ? text : null;
}

const statementRequired = (noticeType, catalogKey) => (
  (noticeType === NOTICE_TYPES.NOT_SATISFIED && catalogKey !== 'financing')
  || noticeType === NOTICE_TYPES.SELLER_CURE
);

function statementHelp(noticeType) {
  if (noticeType === NOTICE_TYPES.NOT_SATISFIED) return contractCopy('WI.composer.statement.help.buyer');
  if (noticeType === NOTICE_TYPES.SELLER_CURE) return contractCopy('WI.composer.statement.help.seller_cure');
  return null;
}

function railSteps(showChoose) {
  return [
    ...(showChoose ? [{ id: 'choose', label: 'Choose' }] : []),
    { id: 'compose', label: 'Write and preview' },
    { id: 'sign', label: 'Sign and deliver' },
  ];
}

/** What a screen reader hears when the composer moves to a step (or the preview arrives). */
export function stepAnnouncement(step, showChoose, hasPreview = false) {
  if (step === 'done') return 'Notice delivered.';
  const steps = railSteps(showChoose);
  const index = steps.findIndex((s) => s.id === step);
  if (index < 0) return '';
  const base = `Step ${index + 1} of ${steps.length}: ${steps[index].label}.`;
  return step === 'compose' && hasPreview ? `${base} Preview ready: review the exact text that will be delivered.` : base;
}

function StepRail({ step, showChoose }) {
  const steps = railSteps(showChoose);
  const current = steps.findIndex((s) => s.id === step);
  return (
    <ol className={styles.rail} aria-label="Steps">
      {steps.map((s, i) => (
        <li
          key={s.id}
          className={`${styles.railStep} ${i < current || step === 'done' ? styles.railDone : ''} ${i === current ? styles.railCurrent : ''}`}
          aria-current={i === current ? 'step' : undefined}
        >
          <span className={styles.railNum} aria-hidden="true">{i + 1}</span>
          {s.label}
        </li>
      ))}
    </ol>
  );
}

function ErrorBox({ info, contract, children }) {
  if (!info) return null;
  return (
    <div className={styles.errorBox} role="alert" data-testid="composer-error" data-code={info.code || undefined}>
      <p>{composerErrorMessage(info, contract)}</p>
      {children}
    </div>
  );
}

export default function NoticeComposerModal({ contingency, role, initialType, onClose, onDelivered }) {
  const contract = contingency.contract || {};
  const allowed = contract.allowed_actions || [];
  const buyerChoices = role === 'buyer' ? BUYER_CHOICES.filter((t) => allowed.includes(t)) : [];
  const showChoose = !initialType && buyerChoices.length > 1;

  const [step, setStep] = useState(showChoose ? 'choose' : 'compose');
  const [noticeType, setNoticeType] = useState(initialType || (buyerChoices.length === 1 ? buyerChoices[0] : null));
  const [statement, setStatement] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [ack, setAck] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [consent, setConsent] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [pdfError, setPdfError] = useState(null);

  const takesStatement = noticeType && !STATEMENT_NOT_ALLOWED.includes(noticeType);
  const takesAttachments = noticeType && !ATTACHMENTS_NOT_ALLOWED.includes(noticeType);
  const needsStatement = statementRequired(noticeType, contract.catalog_key);
  const buyerNotice = BUYER_CHOICES.includes(noticeType);
  const remaining = msUntil(contract.deadline);
  // Buyer deadline in under a day: warn in red from the first step (design §5.3).
  const under24h = (noticeType ? buyerNotice : role === 'buyer') && remaining != null && remaining > 0 && remaining < DAY_MS
    && contract.deadline_basis === 'contract_days';

  const warnings = useMemo(() => (preview?.warnings || []).filter((w) => !(under24h && w.code === 'under_24h')), [preview, under24h]);
  const ackWarning = warnings.find((w) => w.code === 'attachment_missing_ack');
  const otherMeans = warnings.find((w) => w.code === 'other_means');
  const stepTwoWarnings = warnings.filter((w) => !['attachment_missing_ack', 'other_means', 'no_undo'].includes(w.code));
  const needsAck = Boolean(ackWarning) || error?.code === E.ATTACHMENT_REQUIRED;
  const busy = previewing || delivering || uploading;

  const invalidatePreview = () => { setPreview(null); setAck(false); };

  // What a preview is of: the type, the statement and the attachment ids it
  // was asked for. A response for anything else is stale and dropped.
  const previewInputs = JSON.stringify([
    noticeType,
    takesStatement ? statement : null,
    takesAttachments ? attachments.map((a) => a.file_id) : [],
  ]);
  const previewInputsRef = useRef(previewInputs);
  previewInputsRef.current = previewInputs;
  const previewSeq = useRef(0);

  const finish = useCallback(() => {
    if (result) onDelivered?.(result);
    else onClose?.();
  }, [result, onDelivered, onClose]);

  const closeAndRefresh = useCallback(() => {
    onDelivered?.(null);
  }, [onDelivered]);

  const runPreview = useCallback(async () => {
    const seq = ++previewSeq.current;
    const requested = previewInputs;
    setPreviewing(true);
    setError(null);
    try {
      const data = await previewNotice(contingency.id, {
        noticeType,
        statement: takesStatement ? statement : undefined,
        attachmentIds: takesAttachments ? attachments.map((a) => a.file_id) : [],
        acknowledgeMissingAttachment: ack,
      });
      // Only the newest preview, of what is on screen now, may be signed.
      if (seq !== previewSeq.current || requested !== previewInputsRef.current) return;
      setPreview(data);
    } catch (err) {
      if (seq !== previewSeq.current || requested !== previewInputsRef.current) return;
      setError(noticeErrorInfo(err));
    } finally {
      if (seq === previewSeq.current) setPreviewing(false);
    }
  }, [contingency.id, noticeType, statement, attachments, takesStatement, takesAttachments, ack, previewInputs]);

  const onFiles = async (event) => {
    const files = [...(event.target.files || [])];
    event.target.value = '';
    if (!files.length) return;
    setUploadError(null);
    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      setUploadError(`Attach at most ${MAX_ATTACHMENTS} files.`);
      return;
    }
    const tooBig = files.find((f) => f.size > MAX_UPLOAD_BYTES);
    if (tooBig) {
      setUploadError(`${tooBig.name} is larger than 25 MB.`);
      return;
    }
    setUploading(true);
    invalidatePreview();
    try {
      const uploaded = [];
      for (const file of files) {
        // Sequential: each upload is its own immutable record.
        // eslint-disable-next-line no-await-in-loop
        uploaded.push(await uploadContingencyAttachment(contingency.id, file));
      }
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (err) {
      setUploadError(noticeErrorInfo(err).message);
    } finally {
      setUploading(false);
    }
  };

  const deliver = async () => {
    setDelivering(true);
    setError(null);
    try {
      const data = await deliverNotice(contingency.id, {
        previewToken: preview.preview_token,
        typedName: typedName.trim(),
        attestationVersion: preview.attestation?.version,
        acknowledgeMissingAttachment: ack,
      });
      setResult(data);
      setStep('done');
    } catch (err) {
      const info = noticeErrorInfo(err);
      setError(info);
    } finally {
      setDelivering(false);
    }
  };

  const reviewAgain = async () => {
    setStep('compose');
    setPreview(null);
    await runPreview();
  };

  const downloadPdf = async () => {
    setPdfError(null);
    try {
      await downloadNoticeDocument(result.notice.id);
    } catch (err) {
      setPdfError(noticeErrorInfo(err).message);
    }
  };

  const title = noticeType ? noticeTitle(noticeType) : 'Deliver written notice';
  const retryable = isRetryable(error);
  // QA D2: the signature must be the sender's name as printed in the signed offer.
  const expectedName = preview?.expected_signer_name || '';
  const nameRejected = error?.code === E.SIGNATURE_INVALID && Boolean(error.detail?.expected_name);
  const finalError = Boolean(error) && FINAL_CODES.includes(error.code) && !retryable;

  return (
    <ContractDialog
      title={title}
      eyebrow={contract.heading}
      onClose={step === 'done' ? finish : onClose}
      busy={busy}
      testId="notice-composer"
      wide
      stepKey={step === 'compose' && preview ? 'compose:preview' : step}
      stepAnnouncement={stepAnnouncement(step, showChoose, Boolean(preview))}
    >
      {step !== 'done' && <StepRail step={step} showChoose={showChoose} />}

      {under24h && step !== 'done' && (
        <p className={styles.urgent} role="alert" data-testid="composer-under-24h">
          {contractCopy('WI.composer.warning.under_24h', { deadline_date: deadlineDate(contract) })}
        </p>
      )}

      {step === 'choose' && (
        <fieldset className={styles.choices}>
          <legend className={styles.legend}>What do you want to deliver?</legend>
          {buyerChoices.map((type) => (
            <label key={type} className={`${styles.choice} ${noticeType === type ? styles.choiceOn : ''}`}>
              <input
                type="radio"
                name={`notice-type-${contingency.id}`}
                value={type}
                checked={noticeType === type}
                onChange={() => { setNoticeType(type); invalidatePreview(); }}
              />
              <span>
                <span className={styles.choiceTitle}>{contractCopy(`WI.composer.choice.${type}`)}</span>
                <span className={styles.help}>{contractCopy(`WI.composer.choice.${type}.help`)}</span>
              </span>
            </label>
          ))}
          <div className={styles.actions}>
            <button type="button" className={styles.secondary} onClick={onClose}>Cancel</button>
            <button
              type="button"
              className={styles.primary}
              disabled={!noticeType}
              onClick={() => setStep('compose')}
            >
              Continue
            </button>
          </div>
        </fieldset>
      )}

      {step === 'compose' && (
        <div className={styles.compose}>
          {/* The statement and attachments are locked while a preview is prepared: it must be of what is on screen. */}
          {takesStatement && (
            <div className={styles.field}>
              <label htmlFor={`statement-${contingency.id}`} className={styles.label}>
                {contractCopy('WI.composer.statement.label')}
                {!needsStatement && <span className={styles.optional}> (optional)</span>}
              </label>
              {statementHelp(noticeType) && (
                <p id={`statement-help-${contingency.id}`} className={styles.help}>{statementHelp(noticeType)}</p>
              )}
              <textarea
                id={`statement-${contingency.id}`}
                className={styles.textarea}
                rows={6}
                maxLength={10000}
                value={statement}
                aria-describedby={statementHelp(noticeType) ? `statement-help-${contingency.id}` : undefined}
                aria-required={needsStatement}
                disabled={previewing}
                onChange={(e) => { setStatement(e.target.value); invalidatePreview(); }}
                data-testid="composer-statement"
              />
            </div>
          )}

          {takesAttachments && (
            <div className={styles.field}>
              <label htmlFor={`attach-${contingency.id}`} className={styles.label}>
                {buyerNotice
                  ? contractCopy('WI.composer.attachments.label')
                  : 'Attach documents'}
                {!buyerNotice && <span className={styles.optional}> (optional)</span>}
              </label>
              {noticeType === NOTICE_TYPES.NOT_SATISFIED && contract.catalog_key === 'financing' && (
                <p className={styles.help}>{contractCopy('WI.composer.attachments.help.financing')}</p>
              )}
              <input
                id={`attach-${contingency.id}`}
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                className={styles.file}
                onChange={onFiles}
                disabled={uploading || previewing}
                data-testid="composer-file"
              />
              <p className={styles.hint}>PDF, JPG or PNG, up to 25 MB each. Files can't be removed after upload.</p>
              {uploading && <p className={styles.hint} role="status">Uploading…</p>}
              {uploadError && <p className={styles.inlineError} role="alert">{uploadError}</p>}
              {attachments.length > 0 && (
                <ul className={styles.files} aria-label="Attached files">
                  {attachments.map((a) => (
                    <li key={a.file_id}>
                      <span className={styles.fileName}>{a.filename}</span>
                      <span className={styles.hash}>{`SHA-256 ${String(a.sha256 || '').slice(0, 12)}`}</span>
                      <button
                        type="button"
                        className={styles.linkBtn}
                        disabled={previewing}
                        onClick={() => { setAttachments((prev) => prev.filter((x) => x.file_id !== a.file_id)); invalidatePreview(); }}
                        aria-label={`Don't attach ${a.filename}`}
                      >
                        Don't attach
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <ErrorBox info={error} contract={contract}>
            {retryable && !preview && (
              <button type="button" className={styles.secondary} onClick={runPreview} disabled={busy} data-testid="composer-retry">
                Try again
              </button>
            )}
            {finalError && (
              <button type="button" className={styles.secondary} onClick={closeAndRefresh}>Close and refresh</button>
            )}
          </ErrorBox>

          {!preview && (
            <div className={styles.actions}>
              {showChoose && (
                <button type="button" className={styles.secondary} onClick={() => setStep('choose')}>Back</button>
              )}
              <button
                type="button"
                className={styles.primary}
                onClick={runPreview}
                disabled={busy || (needsStatement && !statement.trim()) || finalError}
                data-testid="composer-preview"
              >
                {previewing ? 'Preparing preview…' : 'Preview notice'}
              </button>
            </div>
          )}

          {preview && (
            <div
              className={styles.preview}
              data-testid="composer-preview-panel"
              data-step-focus
              tabIndex={-1}
              aria-label="Preview of your notice"
              role="group"
            >
              {effectMessage(preview.effect) && (
                <p
                  className={`${styles.effect} ${preview.effect.deal_ended ? styles.effectEnds : ''}`}
                  data-testid="composer-effect"
                >
                  {effectMessage(preview.effect)}
                </p>
              )}
              {stepTwoWarnings.map((w) => (
                <p
                  key={w.code}
                  className={['after_deadline', 'under_24h', 'attachment_missing'].includes(w.code) ? styles.urgent : styles.note}
                  role={['after_deadline', 'under_24h'].includes(w.code) ? 'alert' : undefined}
                  data-testid={`composer-warning-${w.code}`}
                >
                  {w.message}
                </p>
              ))}
              {ackWarning && (
                <label className={styles.ack}>
                  <input
                    type="checkbox"
                    checked={ack}
                    onChange={(e) => setAck(e.target.checked)}
                    data-testid="composer-ack"
                  />
                  <span>{ackWarning.message}</span>
                </label>
              )}
              <figure className={styles.sheet}>
                <figcaption className={styles.sheetCaption}>The exact text that will be delivered</figcaption>
                <pre className={styles.sheetText} data-testid="composer-rendered-text">{preview.rendered_text}</pre>
                <p className={styles.sheetSig}>
                  {String(preview.signature_statement_template || '').replace('{typed_name}', '__________')}
                </p>
              </figure>
              <div className={styles.actions}>
                <button type="button" className={styles.secondary} onClick={invalidatePreview}>Edit</button>
                <button
                  type="button"
                  className={styles.primary}
                  disabled={Boolean(ackWarning) && !ack}
                  onClick={() => { setError(null); setStep('sign'); }}
                  data-testid="composer-continue"
                >
                  Continue to sign
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {step === 'sign' && preview && (
        <div className={styles.sign}>
          {otherMeans && <p className={styles.note} data-testid="composer-other-means">{otherMeans.message}</p>}
          <label className={styles.ack}>
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="composer-consent"
            />
            <span>{preview.attestation?.text}</span>
          </label>
          <div className={styles.field}>
            <label htmlFor={`typed-name-${contingency.id}`} className={styles.label}>
              {contractCopy('WI.composer.typed_name.label')}
            </label>
            {expectedName && (
              <p id={`typed-name-hint-${contingency.id}`} className={styles.hint} data-testid="composer-typed-name-hint">
                {contractCopy('WI.composer.typed_name.hint', { expected_name: expectedName })}
              </p>
            )}
            <input
              id={`typed-name-${contingency.id}`}
              type="text"
              className={styles.signature}
              autoComplete="name"
              maxLength={200}
              placeholder={expectedName || undefined}
              aria-describedby={expectedName ? `typed-name-hint-${contingency.id}` : undefined}
              aria-invalid={nameRejected || undefined}
              value={typedName}
              onChange={(e) => {
                setTypedName(e.target.value);
                // The server named the expected signer; a new name is a new attempt.
                if (nameRejected) setError(null);
              }}
              data-testid="composer-typed-name"
            />
            <p className={styles.sigPreview} aria-live="polite">
              {String(preview.signature_statement_template || '').replace('{typed_name}', typedName.trim() || '__________')}
            </p>
          </div>
          {needsAck && !ackWarning && (
            <label className={styles.ack}>
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="composer-ack" />
              <span>{contractCopy('WI.composer.attachments.missing_ack')}</span>
            </label>
          )}
          <p className={styles.note}>{contractCopy('WI.composer.warning.no_undo')}</p>
          <ErrorBox info={error} contract={contract}>
            {error?.code === E.PREVIEW_EXPIRED && (
              <button type="button" className={styles.secondary} onClick={reviewAgain}>Review the notice again</button>
            )}
            {error?.code === E.PREVIEW_INVALID && (
              <button type="button" className={styles.secondary} onClick={reviewAgain}>Review the notice again</button>
            )}
            {retryable && (
              <button type="button" className={styles.secondary} onClick={deliver} disabled={delivering} data-testid="composer-retry">
                {delivering ? 'Delivering…' : 'Try again'}
              </button>
            )}
            {finalError && (
              <button type="button" className={styles.secondary} onClick={closeAndRefresh}>Close and refresh</button>
            )}
          </ErrorBox>
          <div className={styles.actions}>
            <button type="button" className={styles.secondary} onClick={() => setStep('compose')} disabled={delivering}>
              Back
            </button>
            <button
              type="button"
              className={styles.deliver}
              onClick={deliver}
              disabled={
                delivering || !consent || typedName.trim().length < 2 || finalError
                || error?.code === E.PREVIEW_EXPIRED || (needsAck && !ack)
              }
              data-testid="composer-deliver"
            >
              {delivering ? 'Delivering…' : contractCopy('WI.composer.deliver.button')}
            </button>
          </div>
        </div>
      )}

      {step === 'done' && result && (
        <div className={styles.done} data-testid="composer-confirmation">
          <div className={styles.stamp} aria-hidden="true">Delivered</div>
          <p className={styles.confirmation} role="status" data-step-focus tabIndex={-1}>{result.confirmation?.message}</p>
          {result.other_means?.message && <p className={styles.note}>{result.other_means.message}</p>}
          {pdfError && <p className={styles.inlineError} role="alert">{pdfError}</p>}
          <div className={styles.actions}>
            {result.notice?.id && (
              <button type="button" className={styles.secondary} onClick={downloadPdf} data-testid="composer-download">
                Download the notice (PDF)
              </button>
            )}
            <button type="button" className={styles.primary} onClick={finish}>Done</button>
          </div>
        </div>
      )}
    </ContractDialog>
  );
}
